"use client";

import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { DecodeText } from "@/components/ui/decode-text";

type ContentLockupProps = {
  label?: string;
  title?: string;
  /** Optional secondary heading displayed below title in Arizona Mix at smaller size */
  subhead?: string;
  description?: string;
  className?: string;
  /** When true, uses light mode styling (dark text on light bg). Default is false (light text on dark bg). */
  light?: boolean;
  /** Optional children to render after title (for custom content like featured text before description) */
  children?: ReactNode;
  /** When true, label will use decode animation */
  decodeLabel?: boolean;
  /** When true, decode animation starts immediately without waiting to be in view */
  decodeLabelImmediate?: boolean;
  /** Delay in ms before decode animation starts */
  decodeLabelDelay?: number;
  /** Custom margin for decode inView trigger (e.g. "0px 0px -50% 0px" to trigger when 50% up screen) */
  decodeLabelMargin?: string;
};

export function ContentLockup({
  label,
  title,
  subhead,
  description,
  className,
  light = false,
  children,
  decodeLabel = false,
  decodeLabelImmediate = false,
  decodeLabelDelay = 0,
  decodeLabelMargin,
}: ContentLockupProps) {
  return (
    <section
      className={cn(
        "relative overflow-hidden flex flex-col gap-1 max-w-[590px]",
        className
      )}
    >
      {label && (
        <div
          className={cn(
            "font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em]",
            light ? "text-black/60" : "text-white/40"
          )}
        >
          {decodeLabel ? (
            <DecodeText
              text={label}
              immediate={decodeLabelImmediate}
              delay={decodeLabelDelay}
              inViewMargin={decodeLabelMargin}
            />
          ) : (
            label
          )}
        </div>
      )}
      {title && (
        <h2
          className={cn(
            "font-arizona-mix mt-4 max-w-2xl text-[42px] leading-[1.1] tracking-[-1.5] lg:text-[50px] text-pretty",
            light ? "text-[#0a0a0a]" : "text-white"
          )}
        >
          {title}
        </h2>
      )}
      {subhead && (
        <p
          className={cn(
            "font-arizona-mix mt-6 max-w-xl text-[22px] leading-[1.4] tracking-[-0.5px]",
            light ? "text-[#0a0a0a]" : "text-white"
          )}
        >
          {subhead}
        </p>
      )}
      {children}
      {description && (
        <div
          className={cn(
            "font-arizona-sans mt-4 max-w-2xl text-[16px] leading-6 space-y-4",
            light ? "text-[#0a0a0a]/60" : "text-white/60"
          )}
        >
          {description.split("\n\n").map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      )}
    </section>
  );
}
